import bbox from "@turf/bbox";
import type { PickEvent, Vec3, View } from "eutopia.ts";
import {
  createContext,
  createControl,
  createViewTransition,
  createWorld as createWorldBase,
  terrain,
} from "eutopia.ts";
import type { FeatureCollection, Geometry } from "geojson";
import { $, effect, type Properties, resolve, root, signal } from "signlets";

import { elevationUrl, imageryUrl } from "../configuration";
import { ground } from "../elevation";
import type { Bounds, Drop, Mode, Position, State } from "../model";
import { drop as drop_ } from "./drop";
import { overlay as overlayLayer } from "./overlay";
import { path } from "./path";
import { range } from "./range";
import { target as target_ } from "./target";
import { vehicle } from "./vehicle";
import { boundsToView } from "./view";

export type WorldProperties = {
  state: State | undefined;
  drop: Drop;
  mode: Mode;
  bounds: Bounds | undefined;
  overlay: FeatureCollection<Geometry> | undefined;
  onChangeDrop: (_: Partial<Drop>) => void;
  onDone: () => void;
};

export type World = Awaited<ReturnType<typeof createWorld>>;

export const createWorld = (
  element: HTMLCanvasElement,
  properties: Properties<WorldProperties>,
) =>
  root(async dispose => {
    const context = await createContext(element);

    const { state, drop, mode, bounds, overlay, onChangeDrop, onDone } =
      properties;

    const [baseView, setBaseView] = signal<View>({
      center: [-98.583, 39.833, 0],
      distance: 10000000,
      orientation: [0, -Math.PI / 6, 0],
      fieldOfView: 45,
    });

    const [follow, setFollow] = signal(false);

    const connected = $(() => !!resolve(state));

    effect(() => {
      if (connected()) setFollow(true);
    });

    const position = $(
      () => resolve(state)?.position ?? ([0, 0, 0] satisfies Vec3),
    );

    const targetView = $(() => {
      const view = baseView();

      if (follow())
        return {
          ...view,
          center: position(),
          distance: Math.min(view.distance, 10000),
        };

      return view;
    });

    const view = createViewTransition(targetView);

    const setView = (_: View) => {
      setBaseView(_);
      setFollow(false);
    };

    const onFollow = () => setFollow(true);

    effect(() => {
      const _bounds = resolve(bounds);
      if (!_bounds) return;
      setView(boundsToView(_bounds));
    });

    effect(() => {
      const _overlay = resolve(overlay);
      if (!_overlay) return;
      const [minLon, minLat, maxLon, maxLat] = bbox(_overlay);
      setView(boundsToView([minLon, minLat, maxLon, maxLat]));
    });

    const onChangePoint = (position: Position) => {
      if (resolve(mode) === "start") onChangeDrop({ start: position });
      else if (resolve(mode) === "end") onChangeDrop({ end: position });
      else if (resolve(mode) === "drop") onChangeDrop({ drop: position });
    };

    const onMouseMove = ({ position }: PickEvent) => onChangePoint(position);

    const onClick = async ({ position }: PickEvent) => {
      onChangePoint(await ground(position));
      onDone();
    };

    const target = $(
      () => resolve(state)?.target ?? ([0, 0, 0] satisfies Vec3),
    );

    const world = await createWorldBase(context, {
      view,
      layers: [
        terrain({ elevationUrl, imageryUrl, onMouseMove, onClick }),
        overlayLayer({ overlay }),
        range({ drop }),
        vehicle({ state, onFollow }),
        path({ state }),
        drop_({
          drop,
          editing: mode,
          onChangeDrop,
        }),
        target_({
          position: target,
        }),
      ],
    });

    createControl({ element, world, view: targetView, setView });

    return {
      view,
      setView,
      dispose,
    };
  });
